import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '../components/common';
import OrgOnboardingModal from '../components/onboarding/OrgOnboardingModal';
import { ManageStaffModal, StaffCredentialsModal } from '../components/staff';
import { usePermissions, ROLE_PERMISSIONS } from '../context/PermissionContext';
import AdminDashboard from '../components/dashboard/AdminDashboard';
import { useAgent } from '../context/AgentContext';
import { signOut } from '../services/auth/authApi';
import { submitOrgOnboarding } from '../services/api/organisationApi';
import { STORAGE_KEYS } from '../constants';
import '../ChatPage.css';

/**
 * Organization Admin Page — Visit-Centric Dashboard
 * 
 * Orchestration layer that:
 * 1. Validates session & initializes permissions
 * 2. Handles onboarding flow (if needed)
 * 3. Renders AdminDashboard with permission-driven modules
 * 
 * Existing modules preserved:
 * - Onboarding modal
 * - Manage Staff modal
 * - Staff Credentials modal
 */
function OrgChatInner() {
  const navigate = useNavigate();
  const { initializeAgent } = useAgent();
  const { initPermissions } = usePermissions();
  const [initialized, setInitialized] = useState(false);
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState(null);
  const [showOrgConfirmation, setShowOrgConfirmation] = useState(false);
  const [showManageStaff, setShowManageStaff] = useState(false);
  const [isOnboardingStaff, setIsOnboardingStaff] = useState(false);
  const [showStaffLogin, setShowStaffLogin] = useState(false);
  const [newlyCreatedCreds, setNewlyCreatedCreds] = useState([]);
  const [sessionData, setSessionData] = useState(null);

  // Initialize session, permissions, and agent
  useEffect(() => {
    const loadSession = async () => {
      try {
        const { getSession: fetchSession, storeSession, isTokenExpired: checkTokenExpired } = await import('../services/api/sessionApi');
        
        if (checkTokenExpired()) {
          // Token expired — attempt silent refresh before signing out
          try {
            const { refreshSession } = await import('../services/auth/authApi');
            await refreshSession();
            // Refresh succeeded — continue loading session normally
          } catch (refreshErr) {
            // Refresh failed — token is truly dead, sign out
            console.warn('[OrgPage] Token expired and refresh failed:', refreshErr.message);
            await signOut();
            navigate('/login');
            return;
          }
        }
        
        const session = await fetchSession();
        
        // Verify organization user
        if (session.agent !== 'crowchat') {
          console.error('[OrgPage] Not an organization user. Agent:', session.agent);
          navigate('/access-denied');
          return;
        }
        
        storeSession(session);
        setSessionData(session);
        
        // Schedule proactive token refresh for this session
        import('../services/api/apiClient').then(({ scheduleTokenRefresh }) => {
          scheduleTokenRefresh();
        }).catch(() => {});
        
        // Initialize permission system from session
        initPermissions(session);
        
        // Check onboarding state (only users with MANAGE_ONBOARDING permission)
        const userRole = session.role || 'ADMIN';
        const userPerms = (session.permissions && Array.isArray(session.permissions))
          ? session.permissions
          : (ROLE_PERMISSIONS[userRole] || []);
        if (userPerms.includes('MANAGE_ONBOARDING')) {
          if (session.onboarding_completed === false) {
            setOnboardingRequired(true);
          } else if (session.staff_onboarded === false) {
            setIsOnboardingStaff(true);
            setShowManageStaff(true);
          }
        }
        
        // Initialize agent for existing modules that depend on it
        const groupMapping = { 'crowchat': 'CrowGroup' };
        const userGroup = groupMapping[session.agent] || 'CrowGroup';
        initializeAgent([userGroup]);
        
        setInitialized(true);
      } catch (error) {
        console.error('[OrgPage] Session error:', error);
        
        if (error.message === 'UNAUTHORIZED') {
          await signOut();
          navigate('/login');
        } else if (error.message === 'ACCOUNT_DISABLED') {
          // Staff member has been disabled by admin — force logout
          await signOut();
          navigate('/login', { state: { message: 'Your account has been disabled. Please contact your organisation admin.' } });
        } else if (error.message === 'DEVICE_NOT_APPROVED' || error.message === 'DEVICE_ID_MISSING') {
          // RC-2 (STAFF-DEVICE-LOCK): Cognito already minted tokens before
          // the backend refused this device — sign out before redirecting so
          // a denied device never keeps a working session.
          await signOut();
          navigate('/login');
        } else if (error.message === 'FORBIDDEN') {
          navigate('/access-denied');
        }
      }
    };
    loadSession();
  }, [initializeAgent, navigate, initPermissions]);

  /**
   * Handle org onboarding form submission
   */
  const handleOnboardingSubmit = async (formData) => {
    setOnboardingLoading(true);
    setOnboardingError(null);

    try {
      const result = await submitOrgOnboarding(formData);

      if (result.success) {
        setOnboardingRequired(false);
        localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');

        if (result.data?.organisation_id) {
          localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ID, result.data.organisation_id);
        }

        setShowOrgConfirmation(true);
      } else {
        setOnboardingError(result.error);
      }
    } catch (error) {
      setOnboardingError('An unexpected error occurred. Please try again.');
    } finally {
      setOnboardingLoading(false);
    }
  };

  // Loading state
  if (!initialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 text-sm mt-3">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Onboarding Modal — existing module */}
      <OrgOnboardingModal
        isOpen={onboardingRequired}
        onSubmit={handleOnboardingSubmit}
        isLoading={onboardingLoading}
        error={onboardingError}
        sessionData={sessionData}
      />

      {/* Org Registration Confirmation — existing module */}
      <ConfirmationModal
        isOpen={showOrgConfirmation}
        title="Organisation Registered Successfully"
        message="Your organisation is registered. Let's onboard your staff."
        ctaLabel="Continue"
        icon="🎉"
        onConfirm={() => {
          setShowOrgConfirmation(false);
          setIsOnboardingStaff(true);
          setShowManageStaff(true);
        }}
      />

      {/* Manage Staff Modal — only used during onboarding (non-dismissable until staff added) */}
      <ManageStaffModal
        isOpen={showManageStaff}
        onClose={() => {
          // During onboarding, prevent closing — staff must be added first
          if (!isOnboardingStaff) {
            setShowManageStaff(false);
          }
        }}
        preventClose={isOnboardingStaff}
        onStaffAdded={(data) => {
          const created = data?.created || [];
          if (created.length > 0) {
            setNewlyCreatedCreds(created);
          }
          if (data?.staff_onboarded) {
            localStorage.setItem(STORAGE_KEYS.STAFF_ONBOARDED, 'true');
            // Onboarding complete — modal can now be closed
            setIsOnboardingStaff(false);
            setShowManageStaff(false);
          }
        }}
      />

      {/* Staff Credentials — existing module (inline after bulk create) */}
      <StaffCredentialsModal
        isOpen={newlyCreatedCreds.length > 0}
        credentials={newlyCreatedCreds}
        onClose={() => setNewlyCreatedCreds([])}
      />

      {/* Staff Login — existing module */}
      <StaffCredentialsModal
        isOpen={showStaffLogin}
        onClose={() => setShowStaffLogin(false)}
      />

      {/* Admin Dashboard — visit-centric UI */}
      {!onboardingRequired && (
        <AdminDashboard
          onStaffLogin={() => setShowStaffLogin(true)}
          sessionData={sessionData}
        />
      )}
    </div>
  );
}

// PermissionProvider is now hoisted to App.js and wraps every route, so
// OrgChatInner no longer needs its own local provider wrap. Permissions
// are initialized by ProtectedRoute as part of its session-validate step,
// so any /org/* child route can call usePermissions() directly.
export default OrgChatInner;
